import { connect, useDispatch } from 'react-redux';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { useField } from 'react-final-form';

import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmUsername } from 'APIPackage/KnmModule/interfaces/IKnmUsername';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getDecisionDataCreatedAt,
  getDecisionDataCreatedBy,
  getDecisionDataDecisionId,
  getDecisionDataDeleted,
  getDecisionDataIsTest,
  getDecisionDataResigningRequired,
  getDecisionDataUpdatedAt,
  getDecisionDataUpdatedBy,
  getDecisionMassCancel,
  getDecisionMassDelete,
  getDecisionPublishedStatus,
  getDecisionStatus,
  getKnmIsDecisionStatusTypeCanceled,
  getKnmIsDecisionStatusTypeCantBeCarryOut,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { ROUTE_DECISIONS } from 'CorePackage/MainModule/routes/routes';
import styles from 'KnmPackage/KnmHeaderModule/components/KnmHeader.module.scss';
import { KnmPublishStatusesEnum } from 'APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { decisionDraftDelete } from 'DecisionPackage/MainModule/actions/decisionActions';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';

import { DecisionErrorsIcon } from './DecisionErrorsIcon';
import { DecisionHeaderButtons } from './DecisionHeaderButtons';
import { IKnmMassCancel } from '../../../APIPackage/KnmModule/interfaces/IKnmMassCancel';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';
import { DecisionStatusHint } from '../../../KnmPackage/KnmHeaderModule/components/DecisionStatusHint';

interface IDecisionHeaderProps {
  decisionData: IDecision | undefined;
  clearDraftPassportData: () => void;
}

interface IDecisionHeaderStoreProps {
  status: IKnmStatus | undefined;
  decisionId: string | undefined;
  decisionCreatedAt: string | null | undefined;
  decisionCreatedBy: IKnmUsername | undefined;
  isDecisionDeleted: boolean | undefined;
  decisionUpdatedAt: string | null | undefined;
  decisionUpdatedBy: IKnmUsername | undefined;
  publishedStatus: KnmPublishStatusesEnum | null | undefined;
  isDecisionStatusTypeCantBeCarryOut: boolean;
  isTest: boolean | null | undefined;
  resigningRequired: boolean;
  isDecisionStatusTypeCanceled: boolean;
  decisionMassCancel: IKnmMassCancel | null | undefined;
  decisionMassDelete: IKnmMassDelete | null | undefined;
}

const DecisionHeaderWithoutStore = ({
  status,
  decisionId,
  decisionCreatedAt,
  decisionCreatedBy,
  isDecisionDeleted,
  decisionUpdatedAt,
  decisionUpdatedBy,
  publishedStatus,
  isDecisionStatusTypeCantBeCarryOut,
  isTest,
  resigningRequired,
  decisionData,
  clearDraftPassportData,
  isDecisionStatusTypeCanceled,
  decisionMassCancel,
  decisionMassDelete,
}: IDecisionHeaderStoreProps & IDecisionHeaderProps) => {
  const dispatch = useDispatch();

  const {
    input: { value: isDraft },
  } = useField('draft', { subscription: { value: true } });
  const {
    input: { value: draftSavedAt },
  } = useField('draftSavedAt', { subscription: { value: true } });

  const [isDecisionSignButtonHover, setIsDecisionSignButtonHover] = useState<boolean>(false);

  const handleMouseOverDecisionSignButton = useCallback(() => {
    setIsDecisionSignButtonHover(true);
  }, []);

  const handleMouseOutDecisionSignButton = useCallback(() => {
    setIsDecisionSignButtonHover(false);
  }, []);

  const createdInfoText = useMemo(
    () =>
      decisionCreatedAt &&
      `Создано${decisionCreatedBy ? ` ${decisionCreatedBy.fullName} (${decisionCreatedBy.username})` : ''} ${formatDate(
        {
          date: decisionCreatedAt,
          dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          parseFormat: DATE_FORMAT_ISO_FNS,
        },
      )}. `,
    [decisionCreatedAt, decisionCreatedBy],
  );

  const updatedInfoText = useMemo(
    () =>
      decisionUpdatedAt
        ? `Обновлено${
            decisionUpdatedBy ? ` ${decisionUpdatedBy.fullName} (${decisionUpdatedBy.username})` : ''
          } ${formatDate({
            date: decisionUpdatedAt,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })}. `
        : '',
    [decisionUpdatedAt, decisionUpdatedBy],
  );

  const deletedInfoText = useMemo(
    () =>
      decisionUpdatedAt && isDecisionDeleted
        ? `Удалено${
            decisionUpdatedBy ? ` ${decisionUpdatedBy.fullName} (${decisionUpdatedBy.username})` : ''
          } ${formatDate({
            date: decisionUpdatedAt,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })}. `
        : '',
    [isDecisionDeleted, decisionUpdatedAt, decisionUpdatedBy],
  );

  const restoreAutosaved = useCallback(() => {
    if (decisionData) {
      dispatch(decisionDraftDelete(decisionData.id, clearDraftPassportData, true));
    }
  }, [decisionData, clearDraftPassportData]);

  const massCancelInfoText = useMemo(
    () =>
      isDecisionStatusTypeCanceled && decisionMassCancel?.cancelled
        ? `Мероприятие отменено в результате массовой отмены ${formatDate({
            date: decisionMassCancel.cancelled,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })}`
        : '',
    [isDecisionStatusTypeCanceled, decisionMassCancel],
  );

  const massDeletedDateTime = useMemo(
    () =>
      decisionMassDelete &&
      formatDate({
        date: decisionMassDelete?.deleted,
        dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        parseFormat: DATE_FORMAT_ISO_FNS,
      }),
    [decisionMassDelete],
  );

  const headerAttributes = {
    ...(isDecisionDeleted &&
      status?.code !== KnmErknmStatusCodesEnum.TYPE_CANT_BE_CARRY_OUT && { 'data-danger': true }),
    ...(decisionMassCancel?.cancelled && isDecisionStatusTypeCanceled && { 'data-danger': true }),
  };

  return (
    <div className={styles.Header} {...headerAttributes}>
      <div className={styles.HeaderContent} style={{ width: '100%', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          <Link className={styles.BackButton} to={ROUTE_DECISIONS}>
            <span className={styles.BackButtonIcon} />
          </Link>
          <div className={styles.TitleBlockWrapper}>
            <div className={styles.TitleBlock}>
              <h3 className={styles.Title}>
                {isTest && <span className={styles.TestUserWrapper}>ТЕСТ</span>}
                {`Решение ${decisionId ?? ''}`.trim()}
              </h3>
              {status && (
                <>
                  &nbsp;<span className={styles.Status}>{status.name}</span>
                </>
              )}
              <DecisionStatusHint isHover={isDecisionSignButtonHover} />
              <DecisionErrorsIcon className={styles.Errors} autoHidingTooltip="onHeaderStickOn" />
            </div>
            <div>
              Статус публикации:{' '}
              {publishedStatus === KnmPublishStatusesEnum.PUBLISHED ? 'Опубликовано' : 'Не опубликовано'}
            </div>
          </div>
        </div>
        <DecisionHeaderButtons
          className={styles.Buttons}
          isSelfHidden
          location="form"
          handleMouseOverDecisionSignButton={handleMouseOverDecisionSignButton}
          handleMouseOutDecisionSignButton={handleMouseOutDecisionSignButton}
        />
      </div>
      {resigningRequired && (
        <div className={styles.InfoBlockResigned}>
          <span>Изменения не считаются внесенными в ЕРКНМ, пока не переподписан паспорт.</span>
          <span>
            Для просмотра предыдущей подписанной редакции паспорта необходимо перейти в раздел «Сведения о подписании»
          </span>
        </div>
      )}
      {isDecisionDeleted && decisionMassDelete && (
        <div className={styles.MassDeleteBlock}>
          <span>
            Мероприятие удалено в результате массового удаления группы №{decisionMassDelete.groupId}{' '}
            {massDeletedDateTime} по основанию «{decisionMassDelete.reason.name}»
          </span>
        </div>
      )}
      {(createdInfoText || updatedInfoText || deletedInfoText) && (
        <div className={styles.InfoBlock}>
          <div className={styles.ActionInfoWrapper}>
            {massCancelInfoText && (
              <div className={cn(styles.ActionInfo, styles.MassCancelInfo)}>{massCancelInfoText}</div>
            )}
            <div className={styles.ActionInfo}>
              {createdInfoText}
              {updatedInfoText}
              {deletedInfoText}
            </div>
            {isDraft && (
              <div className={styles.AutoSaveWrapper}>
                <span>
                  Сохранено автоматически{' '}
                  {convertInitialKnmDateToFormDate(draftSavedAt || '', DATE_FORMAT_LONG_MINUTE_FNS)}
                </span>
                <span className={styles.AutoSaveCancelButton} onClick={restoreAutosaved}>
                  Отменить
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {isDecisionStatusTypeCantBeCarryOut && (
        <div className={styles.InfoBlock}>
          <p className={styles.isKnmStatusTypeCantBeCarryOutError}>
            Для возобновления указанного мероприятия необходимо перевести карточку вида контроля в ЕРВК в статус
            «Опубликовано»
          </p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionHeaderStoreProps => ({
  status: getDecisionStatus(state),
  publishedStatus: getDecisionPublishedStatus(state),
  decisionId: getDecisionDataDecisionId(state),
  decisionCreatedAt: getDecisionDataCreatedAt(state),
  decisionCreatedBy: getDecisionDataCreatedBy(state),
  isDecisionDeleted: getDecisionDataDeleted(state),
  decisionUpdatedAt: getDecisionDataUpdatedAt(state),
  decisionUpdatedBy: getDecisionDataUpdatedBy(state),
  isDecisionStatusTypeCantBeCarryOut: getKnmIsDecisionStatusTypeCantBeCarryOut(state),
  isTest: getDecisionDataIsTest(state),
  resigningRequired: getDecisionDataResigningRequired(state),
  isDecisionStatusTypeCanceled: getKnmIsDecisionStatusTypeCanceled(state),
  decisionMassCancel: getDecisionMassCancel(state),
  decisionMassDelete: getDecisionMassDelete(state),
});

export const DecisionHeader = connect(mapStateToProps)(DecisionHeaderWithoutStore);
